import {Controller} from '@hotwired/stimulus';
import {useIntersection} from 'stimulus-use'
import {consola as l} from "consola/basic";
import {backOff} from "../js/backoff";

export default class extends Controller {

    REFRESH_THRESHOLD = 30
    refreshCounter = 0
    refreshLoop = 1
    rendered = false
    passbackRendered = false

    static values = {
        adUnit: String,
        elementId: String,
        rendered: Boolean,
        allowRefresh: {type: Boolean, default: true},
        passback: {type: String, default: null},
        force: {type: Boolean, default: false},
        minWidth: {type: Number, default: 0},
        isSticky: {type: Boolean, default: false}
    }

    connect() {
        useIntersection(this, {
            rootMargin: "100%",
        })

        if (this.forceValue) {

            backOff(() => {
                let advAppear =  this.appear()
                if (!advAppear){
                    throw new Error(`${this.adUnitValue} not ready. Try again...`);
                }
            }, {
                maxDelay: 30_000,
                numOfAttempts: 10,
                startingDelay: 1000,
            }).catch(e => {
                l.warn(e);
            });

        }
    }

    appear() {

        // configs and library not set
        if (!this._getConfig(this.adUnitValue) || !this._checkGoogleTagLoaded() || Math.max(document.documentElement.clientWidth, window.innerWidth || 0) < this.minWidthValue) {
            return false
        }

        // Render Adv
        if (this.rendered === false) {
            this.rendered = true
            this._addEventListener()
            this.slot = this._render()

            if (this.allowRefreshValue) {
                this._checkRefresh()
            }

            return true
        }

        return false

    }


    _checkRefresh() {
        this.refreshInterval = setInterval(() => {

            if (this.refreshLoop >= 50) {
                l.info(this.elementIdValue, 'Stop refresh')
                clearInterval(this.refreshInterval)
                return;
            }

            this.refreshCounter++

            if (window._debug() && this.refreshCounter % 5 === 0) {
                l.info(this.elementIdValue, 'Refresh counter:', this.refreshCounter, 'Is visible:', this.isVisible(), 'Loop:', this.refreshLoop)
            }

            if (this.refreshCounter >= this.REFRESH_THRESHOLD && this.isVisible()) {
                this._refresh()
                this.refreshCounter = 0
                this.refreshLoop++
            }
        }, 1000)
    }

    _refresh() {

        this.element.parentElement.classList.remove('no-background')

        let slot = this.slot
        googletag.cmd.push(function () {
            googletag.pubads().refresh([slot]);
        });
        if (window._debug()) {
            l.info('Slot refreshed:', slot.getSlotElementId())
        }
    }

    _render() {

        let id = this.elementIdValue
        let name = this.configs.name
        let sizes = this.configs.sizes
        let slot

        if (window._debug()) {
            l.info('Ad unit name:', name)
            l.info('Sizes:', sizes.join())
            l.info('Element id:', id)
        }
        const googletag = window.googletag || {};
        googletag.cmd = googletag.cmd || [];

        googletag.cmd.push(function () {
            slot = googletag.defineSlot(name, sizes, id);
            slot.addService(googletag.pubads());

        });

        googletag.cmd.push(function () {
            if (window._debug()) {
                l.info('Gt display: ', id);
            }
            googletag.display(id);
        });

        googletag.pubads().refresh([slot]);

        return slot;
    }

    _getConfig(adUnit) {
        try {
            this.configs = window.adunits[adUnit];
            return true;
        } catch (e) {
            l.warn('Adunit not exist:', adUnit);
            l.info(window.adunits);
            return false
        }
    }

    _checkGoogleTagLoaded() {
        if (typeof window.googletag?.pubads !== 'function') {
            l.warn('Googletag not loaded');
            return false;
        }

        return true;
    }

    _appendPassback() {
        let passbackHtml = `
        <div 
        id="${this.passbackValue}_passback" 
        data-controller="adv" 
        data-adv-ad-unit-value="${this.passbackValue}" 
        data-adv-element-id-value="${this.passbackValue}_passback" 
        data-adv-allow-refresh-value="false" 
        >
        </div>
        `;

        this.element.parentElement.classList.remove('no-background', 'no-style');

        this.element.insertAdjacentHTML('beforebegin', passbackHtml)
    }

    _addEventListener() {

        let callback = (event) => {

            if (!event.isEmpty) {
                this.element.parentElement.classList.add('no-background')
            }

            // add close button in sticky
            if (!event.isEmpty && this.isStickyValue) {
                setTimeout(() => {
                    let sibling = this.element.previousElementSibling;

                    if (!sibling?.classList.contains('cm-close')) {
                        this.element.insertAdjacentHTML('beforebegin', `<i class="cm-ico cm-close" onclick="this.parentElement.remove()"></i>`)
                    }
                }, 10_000)
            }

            // remove close button in sticky
            //if (event.isEmpty && this.isStickyValue) {
            //        let sibling = this.element.previousElementSibling;
            //        if (sibling?.classList.contains('cm-close')) {
            //           sibling.remove();
            //      }
            // }


            if (event.isEmpty && this.passbackValue && this.passbackRendered === false) {
                this._appendPassback();
                l.info(`Set passback for ${this.adUnitValue} with ${this.passbackValue}`);
                this.passbackRendered = true;
            }

            if (window._debug()) {
                l.info('Slot', event.slot.getSlotElementId(), 'finished rendering.');
                l.info('Advertiser ID:', event.advertiserId);
                l.info('Campaign ID:', event.campaignId);
                l.info('Company IDs:', event.companyIds);
                l.info('Creative ID:', event.creativeId);
                l.info('Creative Template ID:', event.creativeTemplateId);
                l.info('Is backfill?:', event.isBackfill);
                l.info('Is empty?:', event.isEmpty);
                l.info('Label IDs:', event.labelIds);
                l.info('Line Item ID:', event.lineItemId);
                l.info('Size:', event.size);
                l.info('Slot content changed?', event.slotContentChanged);
                l.info('Source Agnostic Creative ID:', event.sourceAgnosticCreativeId);
                l.info('Source Agnostic Line Item ID:', event.sourceAgnosticLineItemId);
                l.info('Yield Group IDs:', event.yieldGroupIds);
            }
        }


        window.googletag.pubads().addEventListener('slotRenderEnded', callback);
    }

}
